import ApiRequest from "./ApiRequest";
import {
  FavouriteWordPayload,
  CreateSearch,
  CreatePlanData,
  T10AvgData,
  PROMPT_PAYLOAD,
  CompareSERP,
  KDDifficultyPayloadItem,
  KDDifficultyCreateResItem,
  KDDifficultyRes,
  KDDifficultyList,
  PutKDPlannerPayload,
  PutKDPlanner,
} from "./types";
import { useUserStore } from "../stores/user";
const store = useUserStore();
export const getProjects = async () => {
  const res = await ApiRequest().get(`/api/serp/projects`);
  if (!Array.isArray(res.data?.results)) {
    throw new Error("Something went wrong");
  }
  return res.data.results;
};
export const postCreateKeywordPlan = async (payload: any) => {
  return await ApiRequest().post<CreatePlanData>(`/api/planner/`, payload);
};
export const putUpdateKeywordPlan = async (
  uuid: string,
  payload: PutKDPlannerPayload
) => {
  return await ApiRequest().put<PutKDPlanner>(`/api/planner/${uuid}`, payload);
};
export const deletePlanner = async (plannerID: string) => {
  return await ApiRequest().delete(`/api/planner/${plannerID}`);
};
export const getPlanners = async (
  limit: number,
  page: any,
  next,
  previous,
  filterBy,
  filter?: any
) => {
  try {
    let result;
    let queries: any = {
      limit: limit,
      ...(filterBy === ""
        ? {}
        : { text__isnull: filterBy === "websites" ? true : false }),
      ...(filter ? { q: filter } : {}),
    };
    if (page === "current" && store.KDReportsCurrentPage) {
      const res = await ApiRequest().get(store.KDReportsCurrentPage);
      result = res;
    } else if (page === "next" && next) {
      store.setKDCurrentPageUrl(next);
      const res = await ApiRequest().get(next);
      result = res;
    } else if (page === "previous" && previous) {
      store.setKDCurrentPageUrl(previous);
      const res = await ApiRequest().get(previous);
      result = res;
    } else {
      queries = new URLSearchParams(queries).toString();
      const url = `/api/planner/?${queries}`;
      store.setKDCurrentPageUrl(url);
      const res = await ApiRequest().get(url);
      result = res;
    }
    if (!result) {
      throw new Error("Session has ended, please log in!");
    }
    if (result.status !== 200) {
      throw new Error("Something went wrong!");
    }
    if (!Array.isArray(result.data?.results)) {
      throw new Error("Something went wrong");
    }
    return result.data;
  } catch (error) {
    return error;
  }
};
export const getPlannerDetail = async <T>({
  uuid,
  page_no,
  page_size,
}: {
  uuid: string;
  page_no: number;
  page_size: number;
}) => {
  const res = await ApiRequest().get<T>(
    `/api/planner/${uuid}?page_number=${page_no}&page_size=${page_size}`
  );
  if (res.status !== 200) {
    throw new Error("Something went wrong!");
  }
  return res.data;
};
export const getPlannerData = async () => {
  const res = await ApiRequest().get(`/api/planner/details`);
  if (res.status !== 200) {
    throw new Error("Something went wrong!");
  }
  return res.data;
};
export const postCreateKeywordDifficulty = async (
  payload: KDDifficultyPayloadItem[]
) => {
  return await ApiRequest().post<KDDifficultyCreateResItem[]>(
    `/api/difficulty/`,
    payload
  );
};
export const getKeywordDifficulty = async (discovery_uuid: string) => {
  const res = await ApiRequest().get<KDDifficultyRes>(
    `/api/difficulty/${discovery_uuid}`
  );
  if (res.status !== 200) {
    throw new Error("Something went wrong!");
  }
  return res;
};
export const getKeywordDifficultyList = async (planner_uuid: string) => {
  const res = await ApiRequest().get<KDDifficultyList>(
    `/api/difficulty/?planner_uuid=${planner_uuid}`
  );
  if (res.status !== 200) {
    throw new Error("Something went wrong!");
  }
  return res;
};
export const postSearch = async (postData: CreateSearch) => {
  const res = await ApiRequest().post<{ uuid: string }>(
    `/api/serp/search`,
    postData
  );
  if (res.status !== 201) {
    throw new Error("Something went wrong!");
  }
  return res;
};
export const postBulkSearch = async <T, U>(postData: U[]) => {
  const res = await ApiRequest().post<T[]>(
    `/api/serp/search/spy_bulk_search`,
    postData
  );
  return res.data;
};
export const retrySearch = async (uuid: string) => {
  return await ApiRequest().get<{
    detail: string;
  }>(`/api/serp/search/${uuid}/retry_search`);
};
export const postFavouriteWord = async (
  uuid: string,
  postData: FavouriteWordPayload
) => {
  return await ApiRequest().post<{ result: string }>(
    `/api/analyses/token-analyses/${uuid}/reports/fave_word`,
    postData
  );
};
export const getT10Avg = async (uuid: string) => {
  return await ApiRequest().get<T10AvgData>(
    `/api/serp/search/${uuid}/get_t10_avgs`
  );
};
export const postGLTR = async (text: string) => {
  const postData = {
    text: [text],
    detailed: true,
  };
  return await ApiRequest(false, true).post(
    `https://gltr2.trykeywordspy.com/api/analyze`,
    postData
  );
};
export const detectAiGenerated = async (content: string) => {
  return await ApiRequest(false, true).post(
    `/api/zebraai/detect_ai_generated`, {
      content
    }
  );
};
export const postPrompt = async (postData: PROMPT_PAYLOAD) => {
  return await ApiRequest(false).post(`/api/zebraai/prompt-result`, postData);
};
export const getCompareSerp = async (uuid: string) => {
  return await ApiRequest().get<CompareSERP>(
    `/api/analyses/token-analyses/${uuid}/reports/compare_serp`
  );
};
export const getHeeatQuestions = async () => {
  const res = await ApiRequest().get(`/api/zebraai/heeat_questions`);
  if (res.status !== 200) {
    throw new Error("Something went wrong!");
  }
  return res.data;
};
export const fetchKDUpdate = async (payload: string[]) => {
  try {
    const { data } = await ApiRequest().post("/api/difficulty/fetch_results", {
      difficulty_uuids: payload,
    });
    return data;
  } catch (error) {
    console.error("Error updating keyword difficulty:", error);
    throw error;
  }
};
export const getNewPlannerDetail = async (searchFilter: any) => {
  let searchQuery = "ignore=1";
  if (searchFilter.searchKeyword.trim() != "") {
    searchQuery = searchQuery + `&searchKeyword=${searchFilter.searchKeyword}`;
  }
  if (searchFilter.searchIntent.trim() != "") {
    searchQuery = searchQuery + `&searchIntent=${searchFilter.searchIntent}`;
  }
  if (searchFilter.cpc.min === 0 || searchFilter.cpc.min) {
    searchQuery = searchQuery + `&cpc_min=${searchFilter.cpc.min}`;
  }
  if (searchFilter.cpc.max === 0 || searchFilter.cpc.max) {
    searchQuery = searchQuery + `&cpc_max=${searchFilter.cpc.max}`;
  }
  if (searchFilter.yoy.min === 0 || searchFilter.yoy.min) {
    searchQuery = searchQuery + `&yoy_min=${searchFilter.yoy.min}`;
  }
  if (searchFilter.yoy.max === 0 || searchFilter.yoy.max) {
    searchQuery = searchQuery + `&yoy_max=${searchFilter.yoy.max}`;
  }
  if (searchFilter.words.min === 0 || searchFilter.words.min) {
    searchQuery = searchQuery + `&words_min=${searchFilter.words.min}`;
  }
  if (searchFilter.words.max) {
    searchQuery = searchQuery + `&words_max=${searchFilter.words.max}`;
  }
  if (searchFilter.volume.min === 0 || searchFilter.volume.min) {
    searchQuery = searchQuery + `&volume_min=${searchFilter.volume.min}`;
  }
  if (searchFilter.volume.max === 0 || searchFilter.volume.max) {
    searchQuery = searchQuery + `&volume_max=${searchFilter.volume.max}`;
  }
  if (searchFilter.isFav == true) {
    searchQuery = searchQuery + `&isFav=${searchFilter.isFav}`;
  }
  if (searchFilter.isHidden == true) {
    searchQuery = searchQuery + `&isHidden=${searchFilter.isHidden}`;
  }
  if (searchFilter.isBest == true) {
    searchQuery = searchQuery + `&isBest=${searchFilter.isBest}`;
  }
  if (searchFilter.isCompare == true) {
    searchQuery = searchQuery + `&isCompare=${searchFilter.isCompare}`;
  }
  if (searchFilter.isMost == true) {
    searchQuery = searchQuery + `&isMost=${searchFilter.isMost}`;
  }
  if (searchFilter.isHowTo == true) {
    searchQuery = searchQuery + `&isHowTo=${searchFilter.isHowTo}`;
  }
  if (searchFilter.isAlternative == true) {
    searchQuery = searchQuery + `&isAlternative=${searchFilter.isAlternative}`;
  }
  if (searchFilter.isQuestion == true) {
    searchQuery = searchQuery + `&isQuestion=${searchFilter.isQuestion}`;
  }
  searchQuery =
    searchQuery + `&showDifficultyScore=${searchFilter.showDifficultyScore}`;
  searchQuery =
    searchQuery + `&sortByColumnName=${searchFilter.sortByColumnName}`;
  searchQuery = searchQuery + `&sortOrder=${searchFilter.sortOrder}`;
  searchQuery = searchQuery + `&pageNo=${searchFilter.pageNo}`;
  searchQuery = searchQuery + `&pageSize=${searchFilter.pageSize}`;
  const res = await ApiRequest().get(
    `/api/planner/${searchFilter.planner_uuid}?` + searchQuery
  );
  if (res.status !== 200) {
    throw new Error("Something went wrong!");
  }
  return res.data;
};
export const addRemoveFav = async (
  planner_uuid: string,
  report_names: Array,
  isFav: boolean
) => {
  return await ApiRequest().post<any>(
    `/api/planner/${planner_uuid}/update_reports`,
    { report_names: report_names, is_favorite: isFav }
  );
};
export const addRemoveArchive = async (
  planner_uuid: string,
  report_names: Array,
  isArchive: boolean
) => {
  return await ApiRequest().post<any>(
    `/api/planner/${planner_uuid}/update_reports`,
    { report_names: report_names, is_archive: isArchive }
  );
};
export const getKeywordDifficultyArray = async (payload: string[]) => {
  const res = await ApiRequest().post<KDDifficultyRes>(
    `/api/difficulty/`,
    payload
  );
  if (res.status !== 200) {
    throw new Error("Something went wrong!");
  }
  return res;
};
